import type { I18n } from '@lingui/core';
import { msg, t } from '@lingui/core/macro';
import { z } from 'zod';

import { action, can, defineScreen } from '@/shared/screen/define';
import type { ScreenContext } from '@/shared/screen/types';

import { creditBorrowerFill, creditConditionsFill, creditProductFill, creditProductSearch, hireNext } from './simulation-hire.fill';

// Manifestos de Contratar / Simular (U23, U42). Arquivo leve: entra no `src/app/screens.ts` e no ⌘K. As rotas
// herdam `CreditNote:Read` de `/ccb` (router.tsx:995-1001); Contratar e Crédito pessoal só existem com
// `ENABLE_NEW_SIMULATION === 1` (router.tsx:1078).

export const SIMULATE_PATH = '/ccb/simular';
export const HIRE_PATH = '/ccb/contratar';
export const PERSONAL_CREDIT_PATH = '/ccb/contratar/credito-pessoal';
export const VEHICLE_PATH = '/ccb/contratar/veiculo';
// As jornadas de consignado no slug do rótulo padrão (`formatRoute`, labelConfig.ts:5-48): estável, e o rótulo do
// tenant só no texto. Moram aqui porque os cartões e as ferramentas de Contratar levam a elas.
export const PUBLIC_PAYROLL_PATH = `${HIRE_PATH}/consignado-publico`;
export const PRIVATE_PAYROLL_PATH = `${HIRE_PATH}/consignado-privado`;
export const PRIVATE_CDC_PAYROLL_PATH = `${HIRE_PATH}/consignado-privado-cdc`;
export const INSS_PAYROLL_PATH = `${HIRE_PATH}/consignado-inss`;

export const SIMULATION_READ = can('CreditNote', 'Read');
const NEW_SIMULATION = { flag: { key: 'ENABLE_NEW_SIMULATION', equals: 1 } } as const;

/** Nenhum alvo: simula o formulário aberto (trava Lite, agent-tool-targets). */
export const simulateInput = z.strictObject({});
export type SimulateInput = z.infer<typeof simulateInput>;

const mountedScreenRequired = (): never => {
  throw new Error(t`Abra a tela de simulação para simular.`);
};

/** Prévia padrão; a tela troca pela que lê o formulário (produto, valor, taxa e prazo). */
const simulatePreview = () => ({
  title: t`Simular a operação?`,
  detail: t`Calcula a operação com os parâmetros do formulário.`,
});

const simulateDescription = msg`Calcula a simulação com o produto e os parâmetros que estão no formulário agora e mostra os resultados da operação e as parcelas. Não cria operação. Pede a confirmação da pessoa.`;

export const simulationSimulateRun = action({
  id: 'simulation.simulate.run',
  kind: 'write',
  label: msg`Simular`,
  description: simulateDescription,
  keywords: [msg`simular`, msg`amortização`, msg`parcelas`],
  permission: SIMULATION_READ,
  input: simulateInput,
  preview: simulatePreview,
  run: mountedScreenRequired,
});

export const simulationPersonalCreditRun = action({
  id: 'simulation.personalCredit.run',
  kind: 'write',
  label: msg`Simular`,
  description: simulateDescription,
  keywords: [msg`simular`, msg`crédito pessoal`, msg`FGTS`],
  permission: SIMULATION_READ,
  input: simulateInput,
  preview: simulatePreview,
  run: mountedScreenRequired,
});

export const simulationSimulateScreen = defineScreen({
  id: 'simulation.simulate',
  path: SIMULATE_PATH,
  title: msg`Simular`,
  description: msg`Simulação livre de uma operação de crédito: a pessoa escolhe o produto, preenche os parâmetros (valor, taxa, prazo, datas) e vê os resultados da operação e as parcelas. Daqui ela abre a operação com a simulação.`,
  permission: SIMULATION_READ,
  place: { icon: 'file-text', section: 'credit-note' },
  actions: [simulationSimulateRun],
});

export const simulationHirePersonalCredit = action({
  id: 'simulation.hire.personalCredit',
  kind: 'navigate',
  label: msg`Crédito pessoal`,
  description: msg`Abre a simulação de crédito pessoal, com todos os produtos de crédito.`,
  permission: SIMULATION_READ,
  run: ({ nav }) => nav.to(PERSONAL_CREDIT_PATH),
});

export const simulationHireFgts = action({
  id: 'simulation.hire.fgts',
  kind: 'navigate',
  label: msg`FGTS`,
  description: msg`Abre a simulação de crédito pessoal só com os produtos de antecipação do FGTS.`,
  permission: SIMULATION_READ,
  run: ({ nav }) => nav.to(PERSONAL_CREDIT_PATH, { search: { simulationMode: 'fgts' } }),
});

export const simulationHirePublicPayroll = action({
  id: 'simulation.hire.publicPayroll',
  kind: 'navigate',
  label: msg`Consignado público`,
  description: msg`Abre as jornadas de consignado público: um contrato novo, com compra de dívida ou o refinanciamento.`,
  permission: SIMULATION_READ,
  run: ({ nav }) => nav.to(PUBLIC_PAYROLL_PATH),
});

export const simulationHirePrivatePayroll = action({
  id: 'simulation.hire.privatePayroll',
  kind: 'navigate',
  label: msg`Consignado privado`,
  description: msg`Abre as jornadas de consignado privado (crédito do trabalhador).`,
  permission: SIMULATION_READ,
  run: ({ nav }) => nav.to(PRIVATE_PAYROLL_PATH),
});

export const simulationHirePrivateCdcPayroll = action({
  id: 'simulation.hire.privateCdcPayroll',
  kind: 'navigate',
  label: msg`Consignado privado CDC`,
  description: msg`Abre a contratação de consignado privado CDC: o CPF, a simulação das ofertas e a escolha da oferta.`,
  permission: SIMULATION_READ,
  run: ({ nav }) => nav.to(PRIVATE_CDC_PAYROLL_PATH),
});

export const simulationHireInssPayroll = action({
  id: 'simulation.hire.inssPayroll',
  kind: 'navigate',
  label: msg`Consignado INSS`,
  description: msg`Abre as jornadas de consignado INSS.`,
  permission: SIMULATION_READ,
  run: ({ nav }) => nav.to(INSS_PAYROLL_PATH),
});

export const simulationHireVehicle = action({
  id: 'simulation.hire.vehicle',
  kind: 'navigate',
  label: msg`Veículo`,
  description: msg`Abre as operações com garantia veicular: Car Equity ou Financiamento de Veículo.`,
  permission: SIMULATION_READ,
  run: ({ nav }) => nav.to(VEHICLE_PATH),
});

export const simulationHireScreen = defineScreen({
  id: 'simulation.hire',
  path: HIRE_PATH,
  title: msg`Contratar`,
  description: msg`Escolha da jornada de contratação por produto: consignado público, crédito pessoal, FGTS, consignado privado, consignado privado CDC, consignado INSS e veículo. Cada cartão abre a jornada do produto.`,
  permission: SIMULATION_READ,
  access: NEW_SIMULATION,
  place: { icon: 'file-text', section: 'credit-note', flow: true },
  // Na ordem dos cartões do legado (NewSimulation.tsx:37-98).
  actions: [
    simulationHirePublicPayroll,
    simulationHirePersonalCredit,
    simulationHireFgts,
    simulationHirePrivatePayroll,
    simulationHirePrivateCdcPayroll,
    simulationHireInssPayroll,
    simulationHireVehicle,
  ],
});

const PERSONAL_PREFIX = 'simulation.personalCredit';

/** O Luy no crédito pessoal e no FGTS (U56): produto pela busca, tomador, condições e avançar; o Simular é da pessoa. */
export const personalCreditFills = {
  searchProduct: creditProductSearch(PERSONAL_PREFIX),
  product: creditProductFill(PERSONAL_PREFIX),
  borrower: creditBorrowerFill(PERSONAL_PREFIX),
  conditions: creditConditionsFill(PERSONAL_PREFIX),
  next: hireNext(PERSONAL_PREFIX),
};

export const simulationPersonalCreditScreen = defineScreen({
  id: 'simulation.personalCredit',
  path: PERSONAL_CREDIT_PATH,
  title: msg`Crédito pessoal`,
  description: msg`Simulação de crédito pessoal da jornada Contratar (no modo FGTS, só produtos de FGTS): a pessoa escolhe o produto, preenche os parâmetros e vê os resultados e as parcelas. Daqui ela abre a operação com a simulação.`,
  permission: SIMULATION_READ,
  access: NEW_SIMULATION,
  place: { icon: 'file-text', parent: 'simulation.hire', flow: true },
  actions: [simulationPersonalCreditRun, ...Object.values(personalCreditFills)],
});

export interface SimulationContextInput {
  readonly productName: string | null | undefined;
  readonly fgts: boolean;
  /** Produto de consignado: as consultas de margem ficam no cabeçalho. */
  readonly payroll: boolean;
  readonly simulationId: string | null | undefined;
  readonly numberOfPayments: number | null | undefined;
  /** A etapa à vista no Contratar em etapas (W06); ausente no `/ccb/simular`. */
  readonly step?: string | undefined;
}

/** O que o agente lê da tela (U24, U46): produto, modo e se já há resultado. Sem tomador nem documento. */
export function simulationContext(input: SimulationContextInput, i18n: I18n): ScreenContext {
  const product = input.productName;
  const mode = input.fgts ? i18n._(msg`Modo FGTS (só produtos de FGTS).`) : '';
  const chosen = product
    ? i18n._(msg`Produto escolhido: ${product}.`)
    : i18n._(msg`Nenhum produto escolhido.`);
  const step = input.step;
  const payroll = input.payroll
    ? step
      ? i18n._(msg`Este produto é consignado; as consultas de margem ficam na etapa Condições.`)
      : i18n._(msg`Este produto é consignado; as consultas de margem ficam no cabeçalho da tela.`)
    : '';
  const installments = input.numberOfPayments ?? 0;
  const result = input.simulationId
    ? installments > 0
      ? i18n._(msg`Há uma simulação calculada, com ${installments} parcelas.`)
      : i18n._(msg`Há uma simulação calculada.`)
    : i18n._(msg`Ainda sem simulação calculada.`);
  return {
    summary: [
      step ? i18n._(msg`Contratar em etapas, etapa à vista: ${step}.`) : '',
      i18n._(msg`Simulação de operação de crédito.`),
      mode,
      chosen,
      payroll,
      result,
    ]
      .filter(Boolean)
      .join(' '),
    fields: {
      productChosen: Boolean(product),
      fgts: input.fgts,
      payroll: input.payroll,
      simulated: Boolean(input.simulationId),
      numberOfPayments: input.numberOfPayments ?? null,
      ...(step ? { step } : {}),
    },
    sensitivity: 'internal',
  };
}

/** Contexto do hub (U46): a etapa à vista, o produto marcado e quais jornadas não estão disponíveis. */
export function simulationHireContext(i18n: I18n, chosen?: string | null): ScreenContext {
  const product = chosen ?? '';
  return {
    summary: [
      i18n._(msg`Contratar, etapa 1: Produto.`),
      i18n._(
        msg`Escolha da jornada de contratação. Cada cartão abre a jornada do produto; CCB, Processo trabalhista, Nota comercial e Financiamento estudantil não estão disponíveis.`,
      ),
      product ? i18n._(msg`Produto marcado: ${product}.`) : '',
    ]
      .filter(Boolean)
      .join(' '),
    fields: { step: 'produto', productChosen: product !== '' },
    sensitivity: 'internal',
  };
}
